import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Image as ImageIcon } from 'lucide-react';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import { getFullImageUrl, uploadImage, validateImageFile } from '../../utils/imageUpload';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1a1a2e;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 560px;
  position: relative;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    border-radius: 10px;
    padding: 16px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  position: sticky;
  top: 0;
  background: #1a1a2e;
  z-index: 1;
  padding-bottom: 8px;
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: #ffffff;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
  padding-right: 4px;
  flex: 1;
  min-height: 0; /* allow flex child to shrink for scrolling */
  -webkit-overflow-scrolling: touch; /* smooth scrolling on iOS */
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const TextArea = styled.textarea`
  width: 100%;
  background: #16213e;
  border: 1px solid #2d2d44;
  border-radius: 6px;
  color: #ffffff;
  padding: 10px;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  &:focus { 
    border-color: #00d4ff;
    outline: none;
  }
`;

const ImageUploadContainer = styled.div`
  border: 2px dashed #2d2d44;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  &:hover {
    border-color: #00d4ff;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.05) 0%, transparent 100%);
  }
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 180px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImagePlaceholder = styled.div`
  color: white;
  font-size: 42px;
  opacity: 0.7;
`;

const UploadText = styled.div`
  color: #b8b8d1;
  font-size: 14px;
  margin-bottom: 6px;
`;

const UploadSubtext = styled.div`
  color: #6c6c8a;
  font-size: 12px;
`;

const FileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin-top: 6px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #4ecdc4;
  font-size: 14px;
  margin-top: 6px;
  text-align: center;
`;

const Label = styled.label`
  color: #b8b8d1;
  font-size: 14px;
`;

const Select = styled.select`
  background: #16213e;
  border: 1px solid #2d2d44;
  border-radius: 6px;
  color: #ffffff;
  padding: 10px;
  width: 100%;
  
  &:focus {
    border-color: #00d4ff;
    outline: none;
  }
`;

const AchievementModal = ({ isOpen, onClose, achievement, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points: '',
    triggerType: 0,
    triggerThreshold: '',
    iconUrl: ''
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (achievement) {
      const triggerTypeMapping = {
        TotalKills: 0,
        TotalWins: 1,
        TotalMatchesPlayed: 2,
        ReachSpecificRank: 3,
        KillsInASingleMatch: 4,
        ConsecutiveLosses: 5,
        ConsecutiveDailyPlay: 6,
      };
      const rawTriggerType = achievement.triggerType;
      const mappedTriggerType =
        typeof rawTriggerType === 'string'
          ? (triggerTypeMapping[rawTriggerType] ?? 0)
          : (Number(rawTriggerType) || 0);
      setFormData({
        name: achievement.name || '',
        description: achievement.description || '',
        points: achievement.points?.toString?.() || '',
        triggerType: mappedTriggerType,
        triggerThreshold: achievement.triggerThreshold?.toString?.() || '',
        iconUrl: achievement.iconUrl || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        points: '',
        triggerType: 0,
        triggerThreshold: '',
        iconUrl: ''
      });
    }
  }, [isOpen, achievement]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: (formData.name || '').trim(),
      description: (formData.description || '').trim(),
      iconUrl: (formData.iconUrl || '').trim(),
      points: Number.parseInt(formData.points, 10),
      triggerType: Number(formData.triggerType),
      triggerThreshold: Number.parseInt(formData.triggerThreshold, 10),
    };

    if (Number.isNaN(payload.points) || Number.isNaN(payload.triggerThreshold)) {
      setError('مقادیر عددی را به‌درستی وارد کنید');
      return;
    }

    onSave(payload);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'triggerType' ? Number(value) : value
    }));
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {achievement ? 'ویرایش دستاورد' : 'افزودن دستاورد جدید'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>
        
        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormGroup>
            <Label>نام دستاورد</Label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="نام دستاورد را وارد کنید"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>آیکون دستاورد</Label>
            <ImageUploadContainer onClick={() => document.getElementById('achievement-image-upload').click()}>
              {formData.iconUrl ? (
                <ImagePreview>
                  <img src={getFullImageUrl(formData.iconUrl)} alt="Achievement icon preview" />
                </ImagePreview>
              ) : (
                <ImagePlaceholder>
                  <ImageIcon size={42} />
                </ImagePlaceholder>
              )}
              <UploadText>
                {uploading ? 'در حال آپلود...' : 'برای آپلود آیکون کلیک کنید'}
              </UploadText>
              <UploadSubtext>فرمت‌های مجاز: JPG, PNG, GIF, WebP (حداکثر 5MB)</UploadSubtext>
              <FileInput
                id="achievement-image-upload"
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  try {
                    setError('');
                    setSuccess('');
                    validateImageFile(file);
                    setUploading(true);
                    const path = await uploadImage(file, 'achievement');
                    setFormData(prev => ({ ...prev, iconUrl: path }));
                    setSuccess('تصویر با موفقیت آپلود شد');
                    setTimeout(() => setSuccess(''), 3000);
                  } catch (err) {
                    setError(err.message || 'خطا در آپلود تصویر');
                  } finally {
                    setUploading(false);
                  }
                }}
                disabled={uploading}
              />
            </ImageUploadContainer>
          </FormGroup>

          <FormGroup>
            <Label>توضیحات</Label>
            <TextArea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="توضیحات دستاورد را وارد کنید"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>امتیاز</Label>
            <Input
              name="points"
              type="number"
              value={formData.points}
              onChange={handleChange}
              placeholder="مقدار امتیاز را وارد کنید"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>نوع شرط</Label>
            <Select
              name="triggerType"
              value={formData.triggerType}
              onChange={handleChange}
              required
            >
              <option value={0}>کل کشته‌ها</option>
              <option value={1}>کل بردها</option>
              <option value={2}>کل مسابقات</option>
              <option value={3}>رتبه خاص</option>
              <option value={4}>کشته در یک مسابقه</option>
              <option value={5}>باخت متوالی</option>
              <option value={6}>بازی روزانه متوالی</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>مقدار شرط</Label>
            <Input
              name="triggerThreshold"
              type="number"
              value={formData.triggerThreshold}
              onChange={handleChange}
              placeholder="مقدار شرط را وارد کنید"
              required
            />
          </FormGroup>

          

          

          <Button type="submit" variant="primary">
            {achievement ? 'ویرایش دستاورد' : 'افزودن دستاورد'}
          </Button>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AchievementModal;